<template>
    <div class="card my-5">
        <div class="card-header">
            <h3 class="card-title">MEV Rewards</h3>
        </div>
        <div class="card-body">
            <div v-if="!walletConnected" class="text-center">
                <p class="lead">Connect your wallet to check and claim your MEV rewards</p>
            </div>
            <div v-else>
                <div class="row">
                    <div class="col-md-6">
                        <h4>Claimable MEV Rewards</h4>
                        <p class="lead text-success">{{ totalClaimableMev.toFixed(4) }} SOL</p>
                        <p class="text-muted">(≈ ${{ (totalClaimableMev * solPrice).toFixed(5) }})</p>
                        <el-button
                            type="success"
                            :disabled="totalClaimableMev === 0"
                            @click="claimMevRewards"
                            :loading="claiming"
                        >
                            Claim MEV Rewards
                        </el-button>
                    </div>
                    <div class="col-md-6">
                        <h4>Pending MEV This Epoch</h4>
                        <p class="lead">{{ pendingMev.toFixed(4) }} SOL</p>
                        <p class="text-muted">(≈ ${{ (pendingMev * solPrice).toFixed(5) }})</p>
                        <p class="text-muted">Claimable in {{ timeLeftInEpoch }}</p>
                        <p class="text-muted">Next estimated tip in {{ nextTipCountdown }}</p>
                        <div class="mt-3">
                            <p class="text-muted mb-1">Block Progress</p>
                            <el-progress
                                :percentage="blocks.length/totalMevSlots*100"
                                :format="format => `${format.toFixed(1)}% (${blocks.length}/${totalMevSlots} blocks)`"
                            />
                        </div>
                    </div>
                </div>

                <div class="mt-4">
                    <h4>Recent MEV Blocks</h4>
                    <el-table :data="blocks" style="width: 100%" class="mb-4">
                        <el-table-column prop="slot" label="Slot">
                            <template #default="scope">
                                <a :href="'https://explorer.solana.com/block/' + scope.row.slot" target="_blank">
                                    {{ scope.row.slot }}
                                </a>
                            </template>
                        </el-table-column>
                        <el-table-column prop="leader.leader" label="Validator">
                            <template #default="scope">
                                {{ validatorData[scope.row.leader.leader]?.name || scope.row.leader.leader }}
                            </template>
                        </el-table-column>
                        <el-table-column prop="fees.jito_total" label="Total Tips">
                            <template #default="scope">
                                {{ (scope.row.fees.jito_total / 1e9).toFixed(4) }} SOL
                            </template>
                        </el-table-column>
                        <el-table-column label="Your Share">
                            <template #default="scope">
                                {{ calculateUserShare(scope.row).toFixed(4) }} SOL
                            </template>
                        </el-table-column>
                    </el-table>

                    <h4>Your Stake Accounts</h4>
                    <el-table :data="stakeAccountsWithMev" style="width: 100%">
                        <el-table-column prop="validator.name" label="Validator">
                            <template #default="scope">
                                <div class="d-flex align-items-center">
                                    <img
                                        :src="scope.row.validator.avatar_url"
                                        class="me-2 rounded-circle"
                                        style="width: 32px; height: 32px"
                                    >
                                    {{ scope.row.validator.name }}
                                </div>
                            </template>
                        </el-table-column>
                        <el-table-column prop="mevBalance" label="Claimable MEV">
                            <template #default="scope">
                                {{ scope.row.mevBalance.toFixed(4) }} SOL
                            </template>
                        </el-table-column>
                        <el-table-column prop="pendingMev" label="Pending MEV">
                            <template #default="scope">
                                {{ scope.row.pendingMev.toFixed(4) }} SOL
                            </template>
                        </el-table-column>
                        <el-table-column prop="progress" label="Leader Progress">
                            <template #default="scope">
                                <el-progress
                                    :percentage="scope.row.progress"
                                    :format="format => `${format}%`"
                                />
                            </template>
                        </el-table-column>
                    </el-table>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import {ref, computed, onMounted, onUnmounted, watch} from 'vue'
import {useWallet} from 'solana-wallets-vue'
import {Connection, StakeProgram, Transaction} from '@solana/web3.js'
import axios from 'axios'
import collect from 'collect.js'

const rpcUrl = 'https://rpc.helius.xyz/?api-key=9dc68061-cf58-4805-b99a-861b3e093d37'
const wsUrl = rpcUrl.replace('https', 'wss')
const connection = new Connection(rpcUrl)

export default {
    name: 'MevRewardsClaimer',

    setup() {
        const wallet = useWallet()
        const solPrice = ref(0)
        const claiming = ref(false)
        const nextTipCountdown = ref('')
        const pendingMev = ref(0)
        const stakeAccounts = ref([])
        const validatorData = ref({})
        const epochInfo = ref({})
        const timeLeftInEpoch = ref('')
        const loadingAccounts = ref(true)

        // Get stake accounts
        const getStakeAccounts = async () => {
            if (!wallet.connected.value) return

            loadingAccounts.value = true
            try {
                const stakeAccountInfo = await connection.getParsedProgramAccounts(
                    StakeProgram.programId,
                    {
                        filters: [
                            {
                                memcmp: {
                                    offset: 44,
                                    bytes: wallet.publicKey.value.toString()
                                },
                            },
                            {
                                dataSize: 200
                            }
                        ]
                    }
                )
                stakeAccounts.value = stakeAccountInfo
                console.log('Retrieved stake accounts:', stakeAccountInfo)
            } catch (error) {
                console.error('Error getting stake accounts:', error)
            } finally {
                loadingAccounts.value = false
            }
        }

        // Get validator data
        const getValidators = async () => {
            try {
                const response = await axios.get('https://solanacompass.com/validators/ajax', {
                    headers: {'X-Requested-With': 'XMLHttpRequest'}
                })
                validatorData.value = response.data
            } catch (error) {
                console.error('Error getting validator data:', error)
            }
        }
        const currentEpochStartSlot = ref(0)
        // Get epoch info and time remaining
        const getEpochInfo = async () => {
            try {
                epochInfo.value = await connection.getEpochInfo()
                currentEpochInfo.value = epochInfo.value
                currentEpochStartSlot.value = epochInfo.value.absoluteSlot - epochInfo.value.slotIndex
                const samples = await connection.getRecentPerformanceSamples(60)

                const timePerSlotSamples = samples
                    .filter(sample => sample.numSlots !== 0)
                    .slice(0, 60)
                    .map(sample => sample.samplePeriodSecs / sample.numSlots)

                const avgSlotTime = timePerSlotSamples.reduce((sum, cur) => sum + cur, 0) / timePerSlotSamples.length
                const slotsRemaining = epochInfo.value.slotsInEpoch - epochInfo.value.slotIndex
                const msRemaining = slotsRemaining * avgSlotTime * 1000

                const hours = Math.floor(msRemaining / (1000 * 60 * 60))
                const minutes = Math.floor((msRemaining % (1000 * 60 * 60)) / (1000 * 60))
                timeLeftInEpoch.value = `${hours}h ${minutes}m`

            } catch (error) {
                console.error('Error getting epoch info:', error)
            }
        }

        // Get SOL price
        const getSolPrice = async () => {
            try {
                const response = await axios.get('https://api.coingecko.com/api/v3/simple/price?ids=solana&vs_currencies=usd')
                solPrice.value = response.data.solana.usd
            } catch (error) {
                console.error('Error fetching SOL price:', error)
            }
        }

        // Calculate MEV balances for each stake account
        const stakeAccountsWithMev = computed(() => {
            return stakeAccounts.value.map(account => {
                const activeStake = account.account.data.parsed.info.stake?.delegation?.stake || 0
                const totalBalance = account.account.lamports
                const mevBalance = (totalBalance - activeStake) / 1e9
                const validator = validatorData.value[account.account.data.parsed.info.stake?.delegation?.voter] || {}

                return {
                    ...account,
                    validator,
                    mevBalance,
                    pendingMev: 0,
                    progress: 0
                }
            })
        })

        const totalClaimableMev = computed(() => {
            return stakeAccountsWithMev.value.reduce((total, account) => {
                return total + account.mevBalance
            }, 0)
        })

        // Track epoch and slot info
        const currentEpochInfo = ref(null)
        const currentSlot = ref(0)
        const currentSchedule = ref(null)

        // Subscribe to slot updates via WebSocket
        const subscribeToSlotUpdates = () => {
            const ws = new WebSocket(wsUrl)

            ws.onopen = () => {
                ws.send(JSON.stringify({
                    jsonrpc: '2.0',
                    id: 1,
                    method: 'slotSubscribe'
                }))
            }

            ws.onmessage = (event) => {
                const message = JSON.parse(event.data)
                if (message.method === 'slotNotification') {
                    currentSlot.value = message.params.result.slot

                    // If we've passed this epoch's final slot, refresh epoch info and schedule
                    if (currentEpochInfo.value && currentSlot.value > (currentEpochInfo.value.absoluteSlot + currentEpochInfo.value.slotsInEpoch)) {
                        Promise.all([
                            getEpochInfo(),
                            updateLeaderSchedule()
                        ])
                    }
                }
            }

            ws.onerror = (error) => {
                console.error('WebSocket error:', error)
            }

            ws.onclose = () => {
                console.log('WebSocket closed, reconnecting...')
                setTimeout(subscribeToSlotUpdates, 1000)
            }

            return ws
        }

        // Get leader schedule once per epoch
        const updateLeaderSchedule = async () => {
            try {
                currentSchedule.value = await connection.getLeaderSchedule()
            } catch (error) {
                console.error('Error getting leader schedule:', error)
            }
        }

        // Calculate progress based on cached schedule and slot
        const totalMevSlots = ref(0)
        const nextMevSlot = ref(0)
        const nextTipSlot = ref(Infinity)

        const updateLeaderProgress = () => {
            try {
                if (!currentSchedule.value || !currentSlot.value) return
                console.log('Updating leader progress...')
                const idAccounts = []
                stakeAccountsWithMev.value.forEach(account => {
                    // console.log(account)
                    const validator = account.validator
                    idAccounts.push(validator.account)
                    let validatorSchedule = currentSchedule.value[validator.account];
                    if (!validator || !validatorSchedule) return

                    const totalSlots = validatorSchedule.length
                    const passedSlots = validatorSchedule
                        .filter(index => (index + currentEpochStartSlot.value) <= currentSlot.value).length
                    // console.log({totalSlots, passedSlots, currentEpochStartSlot, validatorSchedule})
                    account.progress = (passedSlots / totalSlots) * 100
                })
                console.log('Progress updated', stakeAccountsWithMev.value)

                // now lets grab the leader schedules for each validator only
                const mevSlots = []
                Object.keys(idAccounts).forEach((id) => {
                    console.log('ID:', id, idAccounts[id], currentSchedule.value[idAccounts[id]])
                    let vSlots = currentSchedule.value[idAccounts[id]] || null
                    if(!vSlots) return
                    vSlots.forEach((idx) => {
                        let slot = idx + currentEpochStartSlot.value
                        if (!mevSlots.includes(slot)) mevSlots.push(slot)

                    })
                })
                totalMevSlots.value = mevSlots.length
                nextTipSlot.value = mevSlots.filter(slot => slot > currentSlot.value)[0]
                console.log('Total MEV slots:', totalMevSlots.value)

            } catch (error) {
                console.error('Error updating leader progress:', error)
            }
        }

        // Store blocks and progress data
        const blocks = ref([])
        const completedBlocks = ref(0)
        const totalBlocks = ref(0)
        const blockProgress = ref(0)

        // Get pending MEV rewards
        const updatePendingMev = async () => {
            console.log('Updating pending MEV rewards...')
            try {
                const firstSlot = epochInfo.value.absoluteSlot - epochInfo.value.slotIndex
                // Get validator account IDs from vote accounts
                const validators = stakeAccountsWithMev.value
                    .map(a => {
                        const voteAccount = a.account.data.parsed.info.stake?.delegation?.voter
                        return validatorData.value[voteAccount]?.account
                    })
                    .filter(Boolean)
                let vals = collect(validators).unique()
                console.log('Validators:', vals)

                if (vals.length === 0) {
                    console.log('No validators found to query blocks for. Skipping...', stakeAccountsWithMev.value)
                    return
                }

                let params = {
                    "filter[slots]": firstSlot + "," + epochInfo.value.absoluteSlot,
                    "filter[leader.leader]": vals.join(','),
                    per_page: 10000
                };
                console.log('Params:', params)
                const response = await axios.get('/blocks/query', {
                    params: params
                })
                console.log('Blocks response:', response.data.data)
                const blockData = response.data.data || []
                blocks.value = blockData

                // Calculate block progress
                const validatorList = stakeAccountsWithMev.value
                    .map(a => validatorData.value[a.account.data.parsed.info.stake?.delegation?.voter])
                    .filter(Boolean)

                let totalScheduledBlocks = 0
                let completedBlockCount = 0

                validatorList.forEach(validator => {
                    if (!currentSchedule.value || !validator.vote_account) return
                    const scheduledBlocks = currentSchedule.value[validator.vote_account] || []
                    totalScheduledBlocks += scheduledBlocks.length
                    completedBlockCount += scheduledBlocks.filter(slot => slot <= currentSlot.value).length
                })

                completedBlocks.value = completedBlockCount
                totalBlocks.value = totalScheduledBlocks
                blockProgress.value = totalScheduledBlocks > 0
                    ? Math.round((completedBlockCount / totalScheduledBlocks) * 100)
                    : 0
                const validatorTips = collect(blockData).groupBy('leader.leader').map(group => {
                    const validator = Object.values(validatorData.value).find(v => v.account === group.first().leader.leader)
                    console.log(validator)
                    const jitoCommission = validator?.jito_commission || 0
                    const commissionMultiplier = 1 - (jitoCommission / 10000)
                    console.log(group.first())
                    console.log(group.sum(slot => slot.fees?.jito_total || 0))
                    return group.sum(slot => slot.fees?.jito_total || 0) * commissionMultiplier
                }).all()
                console.log('Validator tips:', validatorTips)
                const validatorStake = vals.map((account) => {
                    let val = Object.values(validatorData.value).find(v => v.account === account)
                    console.log(val)
                    return {
                        vote_account: val.vote_account,
                        stake: val.active_stake || 0
                    }
                }).pluck('stake', 'vote_account')
                console.log('Validator stake:', validatorStake)
                stakeAccountsWithMev.value.forEach(account => {
                    const voteAccount = account.account.data.parsed.info.stake?.delegation?.voter
                    if (!voteAccount) return

                    const validator = validatorData.value[voteAccount] || null
                    if (!validator) return

                    const tips = validatorTips[validator.account] || 0
                    const totalStake = validatorStake.items[voteAccount] || 0
                    const userStakeShare = account.account.data.parsed.info.stake?.delegation?.stake / totalStake
                    console.log({validator, tips, totalStake, userStakeShare})

                    account.pendingMev = tips * userStakeShare
                })
                console.log('Pending MEV:', stakeAccountsWithMev.value.map(a => a.pendingMev))

                pendingMev.value = stakeAccountsWithMev.value.reduce((sum, acc) => sum + acc.pendingMev, 0)
                pendingMev.value = pendingMev.value / 1e9
            } catch (error) {
                console.error('Error updating pending MEV:', error)
            }
        }

        // Track next tip data
        const avgSlotTime = ref(0)

        // Update next tip data every 5s
        const updateNextTipData = async () => {
            try {
                if (!currentSchedule.value || !currentSlot.value) return

                // const validators = stakeAccountsWithMev.value.map(a => a.validator.vote_account).filter(Boolean)

                /*let closestTipSlot = Infinity
                for (const [validator, slots] of Object.entries(currentSchedule.value)) {
                    if (!validators.includes(validator)) continue

                    const nextSlot = slots.find(slot => slot > currentSlot.value)
                    if (nextSlot && nextSlot < closestTipSlot) {
                        closestTipSlot = nextSlot
                    }
                }
                nextTipSlot.value = closestTipSlot*/

                const samples = await connection.getRecentPerformanceSamples(60)
                avgSlotTime.value = samples.reduce((sum, sample) => {
                    return sum + (sample.samplePeriodSecs / sample.numSlots)
                }, 0) / samples.length

            } catch (error) {
                console.error('Error updating next tip data:', error)
            }
        }

        // Update countdown display every second using cached data
        const updateNextTipCountdown = () => {
            if (nextTipSlot.value === Infinity) {
                nextTipCountdown.value = 'No scheduled blocks'
                return
            }

            const slotsUntilNext = nextTipSlot.value - currentSlot.value
            const secondsUntilNext = slotsUntilNext * avgSlotTime.value

            const minutes = Math.floor(secondsUntilNext / 60)
            const seconds = Math.floor(secondsUntilNext % 60)
            nextTipCountdown.value = `${minutes}m ${seconds}s`
        }

        // Claim MEV rewards
        const claimMevRewards = async () => {
            if (!wallet.connected.value) return

            claiming.value = true
            try {
                const instructions = stakeAccountsWithMev.value
                    .filter(account => account.mevBalance > 0)
                    .map(account => {
                        return StakeProgram.withdraw({
                            stakePubkey: account.pubkey,
                            authorizedPubkey: wallet.publicKey.value,
                            toPubkey: wallet.publicKey.value,
                            lamports: account.mevBalance * 1e9
                        })
                    })

                const {blockhash, lastValidBlockHeight} = await connection.getLatestBlockhash()
                const transaction = new Transaction({
                    feePayer: wallet.publicKey.value,
                    recentBlockhash: blockhash,
                    lastValidBlockHeight
                }).add(...instructions)

                const signed = await wallet.signTransaction(transaction)
                const txid = await connection.sendRawTransaction(signed.serialize())
                await connection.confirmTransaction(txid)

                // Refresh data
                getStakeAccounts()
            } catch (error) {
                console.error('Error claiming MEV rewards:', error)
            } finally {
                claiming.value = false
            }
        }

        // Initialize data
        onMounted(async () => {
            if (wallet.connected.value) {
                await Promise.all([
                    getStakeAccounts(),
                    getValidators(),
                    getEpochInfo(),
                    updateLeaderSchedule(),
                    getSolPrice()
                ])
                updatePendingMev();
            }

            // Start update intervals
            setInterval(updatePendingMev, 60000)
            setInterval(updateLeaderProgress, 5000)
            setInterval(updateNextTipData, 5000)
            setInterval(updateNextTipCountdown, 1000)

            // Subscribe to slot updates
            const ws = subscribeToSlotUpdates()

            // Cleanup WebSocket on component unmount
            onUnmounted(() => {
                if (ws && ws.readyState === WebSocket.OPEN) {
                    ws.close()
                }
            })
        })

        // Watch wallet connection
        watch(
            () => wallet.connected.value,
            async (connected) => {
                if (connected) {
                    await Promise.all([
                        getStakeAccounts(),
                        getValidators(),
                        getEpochInfo(),
                        getSolPrice(),
                        updateLeaderSchedule(),
                    ])
                    updatePendingMev()
                } else {
                    stakeAccounts.value = []
                }
            }
        )

        // Calculate user's share of a block's tips
        const calculateUserShare = (block) => {
            const validator = Object.values(validatorData.value).find(v => v.account === block.leader.leader)
            if (!validator) return 0

            const jitoCommission = validator.jito_commission || 0
            const commissionMultiplier = 1 - (jitoCommission / 10000)
            const tips = block.fees.jito_total / 1e9 * commissionMultiplier

            const account = stakeAccountsWithMev.value.find(a =>
                validatorData.value[a.account.data.parsed.info.stake?.delegation?.voter]?.account === block.leader.leader
            )
            if (!account) return 0

            const totalStake = validator.active_stake || 1
            const userStakeShare = account.account.data.parsed.info.stake?.delegation?.stake / totalStake

            return tips * userStakeShare
        }

        return {
            wallet,
            walletConnected: computed(() => wallet.connected.value),
            solPrice,
            claiming,
            stakeAccountsWithMev,
            totalClaimableMev,
            pendingMev,
            nextTipCountdown,
            timeLeftInEpoch,
            claimMevRewards,
            blocks,
            calculateUserShare,
            currentEpochInfo,
            currentSlot,
            currentSchedule,
            totalMevSlots,
            nextMevSlot,
            avgSlotTime,
            blockProgress,
            totalBlocks,
        }
    }
}
</script>
